import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '@/lib/api';
import { enablePush, disablePush, currentPushStatus, isPushSupported } from '@/lib/push';

const SEEN_KEY = 'seen_announcement_ids';
const POLL_INTERVAL_MS = 30_000; // 30 seconds

function readSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || '[]')); } catch { return new Set(); }
}
function writeSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...set])); } catch { /* ignore */ }
}

/**
 * Live-poll /api/announcements every 30s.
 * When a NEW announcement (not seen before) arrives, trigger a system notification
 * via the service worker AND expose it as `latest` for an in-page banner.
 */
export function useAnnouncements() {
  const [announcements, setAnnouncements] = useState([]);
  const [latest, setLatest] = useState(null); // most-recent unseen
  const [unseenCount, setUnseenCount] = useState(0);
  const [pushStatus, setPushStatus] = useState({ supported: isPushSupported(), permission: 'default', subscribed: false });
  const seenRef = useRef(readSeen());

  const recomputeUnseen = useCallback((items) => {
    setUnseenCount(items.filter((a) => !seenRef.current.has(a.id)).length);
  }, []);

  const fetchNow = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/announcements`);
      const items = res.data.data || [];
      setAnnouncements(items);
      recomputeUnseen(items);

      // Find the newest unseen item
      const unseen = items.find((a) => !seenRef.current.has(a.id));
      if (unseen) {
        setLatest(unseen);
        // Also post an in-app SW notification when the tab is open (nice UX
        // even if the visitor hasn't enabled real push).
        if ('Notification' in window && Notification.permission === 'granted' && navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage({
            type: 'HLN_NOTIFY',
            title: unseen.discount_label ? `🔥 ${unseen.discount_label} — ${unseen.company?.name || 'Booth'}` : unseen.title,
            body: unseen.title,
            tag: `ann-${unseen.id}`,
            url: unseen.coupon_code ? `/redeem/${unseen.coupon_code}` : (unseen.booth?.id ? `/booth/${unseen.booth.id}` : '/'),
            payload: { id: unseen.id, code: unseen.coupon_code },
          });
        }
      }
    } catch (e) {
      // Silently ignore (network hiccups)
    }
  }, [recomputeUnseen]);

  const refreshPushStatus = useCallback(async () => {
    try { setPushStatus(await currentPushStatus()); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchNow();
    refreshPushStatus();
    const iv = setInterval(fetchNow, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [fetchNow, refreshPushStatus]);

  const markSeen = useCallback((id) => {
    seenRef.current.add(id);
    writeSeen(seenRef.current);
    setLatest((cur) => (cur && cur.id === id ? null : cur));
    setUnseenCount((c) => Math.max(0, c - 1));
  }, []);

  const markAllSeen = useCallback(() => {
    announcements.forEach((a) => seenRef.current.add(a.id));
    writeSeen(seenRef.current);
    setLatest(null);
    setUnseenCount(0);
  }, [announcements]);

  // Real VAPID subscription: subscribes AND posts to backend so future pushes work
  // even when the tab is closed.
  const enablePushNotifications = useCallback(async () => {
    const r = await enablePush();
    await refreshPushStatus();
    return r;
  }, [refreshPushStatus]);

  const disablePushNotifications = useCallback(async () => {
    const r = await disablePush();
    await refreshPushStatus();
    return r;
  }, [refreshPushStatus]);

  // Legacy helper — kept for callers that only want the permission prompt.
  const requestPermission = useCallback(async () => {
    if (!('Notification' in window)) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return await Notification.requestPermission();
  }, []);

  return {
    announcements, latest, unseenCount, markSeen, markAllSeen,
    requestPermission,
    pushStatus,
    enablePushNotifications,
    disablePushNotifications,
  };
}
